import { css, keyframes } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import TaskIcon from '@atlaskit/icon/core/task';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import {
	IssueTypeIcon,
	type IssueTypeIconName,
} from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';

type ListViewProps = {
	columnHeaders?: [string, string, string];
	issueTypesIcons?: IssueTypeIconName[];
	columnNames?: string[];
};

/**
 * List/table illustration for the JFind onboard illustration.
 * Renders a table with placeholder rows. Column headers default to
 * Work / Status / Due date but can be overridden.
 */
const TOTAL_ROWS = 6;

export const JfindIllustrationListView = ({
	columnHeaders = ['Work', 'Status', 'Due date'],
	issueTypesIcons,
	columnNames,
}: ListViewProps) => (
	<Box xcss={tableWrapperStyles}>
		{/* Header */}
		<Flex xcss={tableHeaderStyles} alignItems="center">
			<Box xcss={thWorkStyles}>
				<span
					css={css({
						fontWeight: 700,
						fontSize: '14px',
						color: token('color.text.subtle'),
					})}
				>
					{columnHeaders[0]}
				</span>
			</Box>
			<Box xcss={[thColumnBaseStyles, thColumnBorderStyles]}>
				<span
					css={css({
						fontWeight: 700,
						fontSize: '14px',
						color: token('color.text.subtle'),
					})}
				>
					{columnHeaders[1]}
				</span>
			</Box>
			<Box xcss={thColumnBaseStyles}>
				<span
					css={css({
						fontWeight: 700,
						fontSize: '14px',
						color: token('color.text.subtle'),
					})}
				>
					{columnHeaders[2]}
				</span>
			</Box>
		</Flex>
		{/* Rows */}
		<Box xcss={tableBodyStyles}>
			{Array.from({ length: TOTAL_ROWS }, (_, i) => (
				<JfindIllustrationListRow
					key={`${issueTypesIcons?.[i] ?? ''}-${i}`}
					issueTypeIcon={issueTypesIcons?.[i]}
					columnName={columnNames?.[i]}
					isFirstRow={i === 0}
				/>
			))}
			{/* Status dropdown overlay — only shown when columnNames are provided */}
			{columnNames && columnNames.length > 0 && (
				<JfindIllustrationStatusDropdown columnNames={columnNames} />
			)}
			{/* Fade-out overlay on last row */}
			<Box xcss={tableRowFadeStyles} />
		</Box>
	</Box>
);

type ListRowProps = {
	issueTypeIcon?: IssueTypeIconName;
	columnName?: string;
	isFirstRow?: boolean;
};

export const JfindIllustrationListRow = ({
	issueTypeIcon,
	columnName,
	isFirstRow,
}: ListRowProps) => (
	<Flex xcss={tableRowStyles} alignItems="center">
		<Flex xcss={tdWorkStyles} alignItems="center" gap="space.100">
			{issueTypeIcon ? (
				<div css={iconLabelFadeCss} key={issueTypeIcon}>
					<IssueTypeIcon type={issueTypeIcon} />
					<span
						css={css({
							font: token('font.body'),
							fontWeight: token('font.weight.regular'),
							color: token('color.text'),
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						})}
					>
						{issueTypeIcon.charAt(0).toUpperCase() + issueTypeIcon.slice(1)}
					</span>
				</div>
			) : (
				<>
					<TaskIcon label="" color={token('color.icon.disabled')} />
					<Box xcss={tdTitleBarStyles} />
				</>
			)}
		</Flex>
		<Box xcss={tdStatusStyles}>
			{isFirstRow && columnName ? (
				<Flex xcss={statusPillOpenStyles} alignItems="center" gap="space.050">
					<span
						css={css({
							fontSize: '10.554px',
							fontWeight: 700,
							color: token('color.text'),
							textTransform: 'uppercase',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							flex: 1,
						})}
					>
						{columnName}
					</span>
					<ChevronDownIcon label="" color={token('color.icon.subtle')} />
				</Flex>
			) : (
				<Box xcss={statusPillStyles}>
					<Skeleton width="100%" height="7px" />
				</Box>
			)}
		</Box>
		<Box xcss={tdDateStyles}>
			<Box xcss={statusPillStyles}>
				<Skeleton width="100%" height="7px" />
			</Box>
		</Box>
	</Flex>
);

type StatusDropdownProps = {
	columnNames: string[];
};

const getDropdownPillColor = (index: number, total: number): string => {
	if (index === total - 1) return '#B3DF72';
	if (index > 0) return '#8FB8F6';
	return '#dddee1';
};

const DROPDOWN_ITEM_HEIGHT = 38;

const JfindIllustrationStatusDropdown = ({ columnNames }: StatusDropdownProps) => (
	<div
		css={dropdownCss}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		style={{ maxHeight: `${columnNames.length * DROPDOWN_ITEM_HEIGHT + 12}px` }}
	>
		{columnNames.map((name, i) => (
			<Flex
				key={i}
				xcss={[dropdownItemStyles, i === 0 ? dropdownItemSelectedStyles : undefined]}
				alignItems="center"
				gap="space.050"
			>
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, local/ensure-wac-token-usage
					style={{ backgroundColor: getDropdownPillColor(i, columnNames.length) }}
					css={css({
						borderRadius: token('radius.xsmall', '2px'),
						paddingBlock: 'var(--ds-space-050, 4px)',
						paddingInline: 'var(--ds-space-050, 4px)',
						display: 'flex',
						alignItems: 'center',
						overflow: 'hidden',
						maxWidth: '100px',
					})}
				>
					<span
						css={css({
							fontSize: '10.554px',
							fontWeight: 700,
							color: token('color.text'),
							textTransform: 'uppercase',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						})}
					>
						{name || 'STATUS'}
					</span>
				</div>
			</Flex>
		))}
	</div>
);

const dropdownItemStyles = xcss({
	height: '38px',
	paddingInline: 'space.150',
	paddingBlock: 'space.075',
	cursor: 'default',
});

const dropdownFadeSlideInKeyframes = keyframes({
	from: { opacity: 0, transform: 'translateY(-6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const dropdownCss = css({
	animationName: dropdownFadeSlideInKeyframes,
	animationDuration: '200ms',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'forwards',
	borderRadius: token('radius.large', '8px'),
	boxShadow: token('elevation.shadow.overlay'),
	zIndex: 1,
	overflow: 'hidden',
	transition: 'max-height 200ms ease',
	position: 'absolute',
	insetBlockStart: '46px',
	insetInlineStart: '280px',
	width: '135px',
	paddingBlockStart: '5.739px',
	paddingBlockEnd: '12px',
	backgroundColor: wacToken('color.neutral.light0'),
});

const dropdownItemSelectedStyles = xcss({
	backgroundColor: 'color.background.selected',
	borderInlineStart: `1.913px solid ${token('color.border.selected')}`,
});

const tableWrapperStyles = xcss({
	marginInlineStart: 'space.400',
	marginInlineEnd: 'space.0',
	border: `1.1px solid ${token('color.border')}`,
	borderStartStartRadius: token('radius.large', '8px'),
	borderEndStartRadius: token('radius.large', '8px'),
	overflowX: 'auto',
	overflowY: 'hidden',
	flexShrink: 0,
	position: 'relative',
	flex: 1,
});

const tableHeaderStyles = xcss({
	minWidth: '564px',
	backgroundColor: 'elevation.surface.sunken',
	borderBlockEnd: `1.1px solid ${token('color.border')}`,
	height: '46px',
});

const thWorkStyles = xcss({
	width: '280px',
	paddingInline: 'space.100',
	borderInlineEnd: `1.3px solid ${token('color.border')}`,
	flexShrink: 0,
	height: '100%',
	display: 'flex',
	alignItems: 'center',
});

const thColumnBaseStyles = xcss({
	width: '142px',
	paddingInline: 'space.100',
	flexShrink: 0,
	height: '100%',
	display: 'flex',
	alignItems: 'center',
});

const thColumnBorderStyles = xcss({
	borderInlineEnd: `1.3px solid ${token('color.border')}`,
});

const tableBodyStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
});

const tableRowStyles = xcss({
	minWidth: '564px',
	borderBlockEnd: `1.1px solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface',
	height: '46px',
});

const tdWorkStyles = xcss({
	width: '280px',
	height: '100%',
	paddingInline: 'space.100',
	borderInlineEnd: `1.1px solid ${token('color.border')}`,
	flexShrink: 0,
});

const tdTitleBarStyles = xcss({
	height: '7px',
	flex: 1,
	backgroundColor: 'color.background.neutral',
	borderStartStartRadius: token('radius.xsmall', '2px'),
	borderStartEndRadius: token('radius.xsmall', '2px'),
	borderEndStartRadius: token('radius.xsmall', '2px'),
	borderEndEndRadius: token('radius.xsmall', '2px'),
	minWidth: '0px',
});

const tdStatusStyles = xcss({
	minWidth: '142px',
	maxWidth: '142px',
	height: '100%',
	paddingInline: 'space.100',
	borderInlineEnd: `1.1px solid ${token('color.border')}`,
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
});

const statusPillOpenStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
	borderStartStartRadius: token('radius.small', '3px'),
	borderStartEndRadius: token('radius.small', '3px'),
	borderEndStartRadius: token('radius.small', '3px'),
	borderEndEndRadius: token('radius.small', '3px'),
	paddingBlock: 'space.075',
	paddingInline: 'space.075',
	border: `1.251px solid ${token('color.border')}`,
	overflow: 'hidden',
	width: '100%',
});

const statusPillStyles = xcss({
	border: `1.4px solid ${token('color.border')}`,
	borderStartStartRadius: token('radius.small', '3px'),
	borderStartEndRadius: token('radius.small', '3px'),
	borderEndStartRadius: token('radius.small', '3px'),
	borderEndEndRadius: token('radius.small', '3px'),
	padding: 'space.100',
	display: 'flex',
	alignItems: 'center',
	width: '100%',
});

const tdDateStyles = xcss({
	minWidth: '142px',
	height: '100%',
	paddingInline: 'space.100',
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
});

const iconLabelFadeKeyframes = keyframes({
	from: { opacity: 0, transform: 'translateY(-6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const iconLabelFadeCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: 'var(--ds-space-100, 8px)',
	animationName: iconLabelFadeKeyframes,
	animationDuration: '200ms',
	animationTimingFunction: 'ease-out',
	overflow: 'hidden',
});

const tableRowFadeStyles = xcss({
	position: 'absolute',
	insetBlockEnd: '0',
	insetInlineStart: '0',
	width: '100%',
	minWidth: '564px',
	height: '27px',
	pointerEvents: 'none',
	background:
		'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,1) 100%)' as Parameters<
			typeof xcss
		>[0]['background'],
});
